import { useEffect } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { api } from './api/client';
import { CookieBanner } from './components/CookieBanner';
import { Headset, LogOut, LayoutGrid, Loader2, User as UserIcon, Inbox, Building2, Settings as SettingsIcon, HelpCircle, Lightbulb, BookOpen, Sparkles, TrendingUp, Rocket, Handshake } from 'lucide-react';
import { AuthProvider, useAuth } from './lib/auth';
import { ThemeProvider } from './lib/theme';
import { ThemeToggle } from './components/ThemeToggle';
import TicketList from './pages/TicketList';
import TicketCreate from './pages/TicketCreate';
import TicketDetail from './pages/TicketDetail';
import BoardPage from './pages/Board';
import QueuePage from './pages/Queue';
import OrganizationsPage from './pages/Organizations';
import OrganizationDetail from './pages/OrganizationDetail';
import SettingsPage from './pages/Settings';
import HelpPage from './pages/Help';
import FeaturesPage from './pages/Features';
import KnowledgeBasePage from './pages/KnowledgeBase';
import AIAssistantPage from './pages/AIAssistant';
import DashboardPage from './pages/Dashboard';
import RiskDigestPage from './pages/RiskDigest';
import HDECoveragePage from './pages/HDECoverage';
import MyOverviewPage from './pages/MyOverview';
import PilotsPage from './pages/Pilots';
import PartnersPage from './pages/Partners';
import LoginPage from './pages/Login';
import ActivatePage from './pages/Activate';
import ForgotPage from './pages/Forgot';
import SsoFinishPage from './pages/SsoFinish';
import SecretViewPage from './pages/SecretView';
import { NotificationsBell } from './components/NotificationsBell';

// Версия — инжектируется vite-конфигом из ../VERSION (см. vite.config.ts).
declare const __APP_VERSION__: string;
const APP_VERSION = __APP_VERSION__;

// Главная страница: staff видит очередь, client — список карточек.
function HomeRoute() {
  const { user } = useAuth();
  return user?.type === 'staff' ? <QueuePage /> : <TicketList />;
}

function IconNav({ to, title, children }: { to: string; title: string; children: React.ReactNode }) {
  return (
    <NavLink to={to} title={title}
      className={({ isActive }) =>
        `p-2 rounded-full transition ${
          isActive
            ? 'bg-accent text-white'
            : 'text-fg-muted hover:text-fg hover:bg-surface-2'
        }`
      }>
      {children}
    </NavLink>
  );
}


function ProtectedShell() {
  const { user, logout, loading } = useAuth();
  const loc = useLocation();

  // Page-view трекер: на каждое изменение pathname шлём beacon на /api/v1/audit/track.
  // Best-effort, ошибки игнорируем — не должно ломать навигацию.
  useEffect(() => {
    if (!user) return;
    if (loc.pathname.startsWith('/login') || loc.pathname.startsWith('/activate')) return;
    api.post('/api/v1/audit/track', {
      path: loc.pathname,
      title: document.title?.slice(0, 200) || null,
      referrer: document.referrer?.slice(0, 400) || null,
    }).catch(() => { /* noop */ });
  }, [loc.pathname, user]);

  // Heartbeat для поддержания audit-сессии (online-статус). Первый — сразу
  // после mount (важно для уже залогиненных юзеров, у которых нет открытой
  // сессии — heartbeat-endpoint создаст её), далее каждые 60 секунд.
  // Сессия idle-sweep'ится бэкендом через 5 минут без heartbeat.
  useEffect(() => {
    if (!user) return;
    const tick = () => api.post('/api/v1/audit/heartbeat', {}).catch(() => { /* noop */ });
    tick();
    const t = window.setInterval(tick, 60_000);
    return () => window.clearInterval(t);
  }, [user]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-bg">
      <Loader2 className="animate-spin text-accent" />
    </div>;
  }
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;

  const tabClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-1.5 px-2 md:px-3 py-1.5 rounded text-xs font-bold uppercase transition ${
      isActive ? 'bg-accent text-white' : 'bg-surface-2 text-fg-muted hover:text-fg'
    }`;

  return (
    <div className="min-h-screen bg-bg text-fg">
      <header className="sticky top-0 z-40 bg-surface/90 backdrop-blur-md border-b border-border px-3 md:px-4 py-2 md:py-4 flex flex-wrap justify-between items-center gap-2 shadow-xl">
        <Link to="/" className="flex items-center gap-2 md:gap-3 shrink-0">
          <Headset className="text-accent w-6 h-6 md:w-7 md:h-7" />
          <div>
            <h1 className="text-sm md:text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-500 bg-clip-text text-transparent uppercase tracking-tight font-mono leading-none">
              Service Desk
            </h1>
            <span className="hidden md:block text-[10px] font-bold text-fg-subtle mt-1 tracking-widest uppercase">v{APP_VERSION}</span>
          </div>
        </Link>

        <nav className="flex gap-1 md:gap-2 order-3 md:order-2 w-full md:w-auto justify-center overflow-x-auto">
          <NavLink to="/" end className={tabClass}>
            <Inbox size={14} /> <span className="hidden sm:inline">{user.type === 'staff' ? 'Очередь' : 'Тикеты'}</span>
          </NavLink>
          {user.type === 'client' && (
            <NavLink to="/my" className={tabClass}>
              <Building2 size={14} /> <span className="hidden sm:inline">Моя инсталляция</span>
            </NavLink>
          )}
          <NavLink to="/board" className={tabClass}><LayoutGrid size={14} /> <span className="hidden sm:inline">Канбан</span></NavLink>
          {user.type === 'staff' && (
            <NavLink to="/organizations" className={tabClass}><Building2 size={14} /> <span className="hidden sm:inline">Организации</span></NavLink>
          )}
          <NavLink to="/kb" className={tabClass}><BookOpen size={14} /> <span className="hidden sm:inline">База знаний</span></NavLink>
          <NavLink to="/ai" className={tabClass}><Sparkles size={14} /> <span className="hidden sm:inline">AI</span></NavLink>
          {user.type === 'staff' && (
            <NavLink to="/dashboard" className={tabClass}><TrendingUp size={14} /> <span className="hidden sm:inline">Дашборд</span></NavLink>
          )}
          {user.type === 'staff' && (
            <NavLink to="/pilots" className={tabClass}><Rocket size={14} /> <span className="hidden sm:inline">Пилоты</span></NavLink>
          )}
          {/* «Партнёры» убраны из меню (v0.9.67) — это вопрос продавцов, у них
              B24. Информация о партнёре конкретного клиента доступна в его
              карточке (секция «Партнёр» вкладки «Информация»). */}
        </nav>

        <div className="flex items-center gap-1.5 md:gap-2 order-2 md:order-3">
          <ThemeToggle />
          <IconNav to="/features" title="Идеи"><Lightbulb size={17} /></IconNav>
          {user.type === 'staff' && (
            <IconNav to="/settings" title="Настройки"><SettingsIcon size={17} /></IconNav>
          )}
          <IconNav to="/help" title="Помощь"><HelpCircle size={17} /></IconNav>
          <NotificationsBell />
          <div className="hidden lg:block text-right">
            <div className="text-xs text-fg flex items-center gap-1.5"><UserIcon size={12} />{user.full_name || user.email}</div>
            <div className="text-[10px] text-fg-subtle uppercase tracking-wider">
              {user.type === 'staff' ? user.role : 'клиент'}
            </div>
          </div>
          <button onClick={logout} title="Выйти"
            className="p-2 text-fg-muted hover:text-red-400 transition hover:bg-surface-2 rounded-full">
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto p-3 md:p-6">
        <Routes>
          <Route path="/" element={<HomeRoute />} />
          <Route path="/queue" element={<Navigate to="/" replace />} />
          <Route path="/board" element={<BoardPage />} />
          <Route path="/new" element={<TicketCreate />} />
          <Route path="/tickets/:id" element={<TicketDetail />} />
          <Route path="/organizations" element={<OrganizationsPage />} />
          <Route path="/organizations/:id" element={<OrganizationDetail />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/features" element={<FeaturesPage />} />
          <Route path="/kb" element={<KnowledgeBasePage />} />
          <Route path="/kb/:id" element={<KnowledgeBasePage />} />
          <Route path="/ai" element={<AIAssistantPage />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/hde-coverage" element={<HDECoveragePage />} />
          <Route path="/my" element={<MyOverviewPage />} />
          <Route path="/risk" element={<RiskDigestPage />} />
          <Route path="/pilots" element={<PilotsPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/downloads" element={<Navigate to="/kb?section=downloads" replace />} />
          <Route path="/help" element={<HelpPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function LoginRoute() {
  const { user, loading } = useAuth();
  if (loading) return null;
  // Если уже залогинен — сразу на главную (вместо застревания на /login).
  if (user) return <Navigate to="/" replace />;
  return <LoginPage />;
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/forgot" element={<ForgotPage />} />
          <Route path="/auth/sso-finish" element={<SsoFinishPage />} />
          <Route path="/secret/:id" element={<SecretViewPage />} />
          <Route path="/*" element={<ProtectedShell />} />
        </Routes>
        <CookieBanner />
      </AuthProvider>
    </ThemeProvider>
  );
}
